import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export interface CloudAuthor {
  name: string;
  slug: string;
  count: number;
}

interface CloudWord {
  text: string;
  slug: string;
  count: number;
  size: number;
  rotate: 0 | 90 | -90;
  font: string;
  weight: number;
  colorClass: string;
}

interface PlacedWord extends CloudWord {
  // Centre of the word's glyph box, relative to the circle's centre.
  x: number;
  y: number;
  // Baseline shift that centres the actual glyphs (not the em box) on x/y.
  baselineOffset: number;
}

interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

const SERIF = 'Georgia, "Times New Roman", serif';
const SANS = 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

// Pink / purple / yellow / teal, like the reference design. Dark mode uses
// the bright tints on a deep navy circle; light mode uses deeper shades of
// the same hues so every name keeps readable contrast on white.
const COLOR_CLASSES = [
  "fill-pink-600 dark:fill-pink-400",
  "fill-violet-700 dark:fill-violet-400",
  "fill-amber-600 dark:fill-amber-300",
  "fill-teal-700 dark:fill-teal-300",
];

// Stable per-name hash, so each author keeps the same color, font and
// rotation on every render instead of reshuffling.
function hashString(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

// Circle diameter: the full width on phones/tablets, capped on desktop so
// the whole circle fits on screen.
function diameterFor(width: number): number {
  return Math.floor(Math.min(width, 860));
}

function buildWords(authors: CloudAuthor[], diameter: number): CloudWord[] {
  if (!authors.length) return [];
  const counts = authors.map((a) => a.count);
  const minCount = Math.min(...counts);
  const maxCount = Math.max(...counts);
  const maxFont = clamp(diameter / 10.5, 20, 80);
  const minFont = clamp(diameter / 62, 9, 14);

  return (
    authors
      .map((author) => {
        const hash = hashString(author.slug || author.name);
        // Linear scale: the most-quoted authors stand out and the long tail
        // stays small, which is what lets ~60+ names share one circle.
        const ratio = maxCount === minCount ? 0.4 : (author.count - minCount) / (maxCount - minCount);
        // Cap so the longest names still fit across the circle.
        const fitCap = (diameter * 0.8) / Math.max(1, author.name.length * 0.58);
        const size = Math.round(Math.min(minFont + ratio * (maxFont - minFont), fitCap));
        // About a fifth of the shorter names run vertically, as in the reference.
        const rotate: CloudWord["rotate"] =
          author.name.length <= 18 && hash % 5 === 0 ? (hash % 10 === 0 ? -90 : 90) : 0;
        return {
          text: author.name,
          slug: author.slug,
          count: author.count,
          size,
          rotate,
          font: hash % 3 === 0 ? SANS : SERIF,
          weight: size > minFont * 1.8 ? 700 : 600,
          colorClass: COLOR_CLASSES[hash % COLOR_CLASSES.length],
        };
      })
      // Biggest first, so the prominent names claim the centre.
      .sort((a, b) => b.size - a.size || a.text.localeCompare(b.text))
      // The three biggest names stay horizontal and readable at the centre;
      // a huge vertical word would split the circle in two.
      .map((word, index) => (index < 3 ? { ...word, rotate: 0 as const } : word))
  );
}

// Places words inside a circle: each word walks an Archimedean spiral out
// from the centre and takes the first spot where its whole box is inside the
// circle and clear of every placed word. A uniform grid keeps the overlap
// checks cheap. Words that find no spot are left out (the page lists them).
function layoutInCircle(words: CloudWord[], diameter: number): PlacedWord[] {
  const context = document.createElement("canvas").getContext("2d");
  if (!context) return [];

  const radius = diameter / 2;
  const padding = diameter < 500 ? 2 : 3;
  const cellSize = 32;
  const grid = new Map<string, Rect[]>();
  const placed: PlacedWord[] = [];

  const cellsFor = (rect: Rect) => {
    const keys: string[] = [];
    for (let gx = Math.floor(rect.left / cellSize); gx <= Math.floor(rect.right / cellSize); gx += 1) {
      for (let gy = Math.floor(rect.top / cellSize); gy <= Math.floor(rect.bottom / cellSize); gy += 1) {
        keys.push(`${gx},${gy}`);
      }
    }
    return keys;
  };

  const limit = (radius - padding) * (radius - padding);
  const insideCircle = (rect: Rect) =>
    [
      [rect.left, rect.top],
      [rect.right, rect.top],
      [rect.left, rect.bottom],
      [rect.right, rect.bottom],
    ].every(([x, y]) => x * x + y * y <= limit);

  const collides = (rect: Rect) =>
    cellsFor(rect).some((key) =>
      (grid.get(key) || []).some(
        (other) => rect.left < other.right && rect.right > other.left && rect.top < other.bottom && rect.bottom > other.top
      )
    );

  for (const word of words) {
    context.font = `${word.weight} ${word.size}px ${word.font}`;
    const metrics = context.measureText(word.text);
    const textWidth = metrics.width;
    // Real glyph height (covers Tamil vowel signs above/below too), so rows
    // pack tightly instead of reserving the whole em box.
    const ascent = metrics.actualBoundingBoxAscent || word.size * 0.8;
    const descent = metrics.actualBoundingBoxDescent || word.size * 0.25;
    const textHeight = ascent + descent;
    const baselineOffset = (ascent - descent) / 2;
    const [boxW, boxH] = word.rotate === 0 ? [textWidth, textHeight] : [textHeight, textWidth];
    const halfW = boxW / 2 + padding;
    const halfH = boxH / 2 + padding;
    if (halfW * 2 > diameter || halfH * 2 > diameter) continue;

    // Alternate spiral direction per word so the cloud grows evenly.
    const direction = hashString(word.slug) % 2 === 0 ? 1 : -1;
    const spacing = 2; // px the spiral moves outwards per radian
    let found: Rect | null = null;
    let cx = 0;
    let cy = 0;

    // The spiral is squashed (wider than tall, as names are wide), so it runs
    // to radius / 0.9 to still reach the top and bottom of the circle.
    for (let theta = 0; spacing * theta < radius / 0.9; ) {
      const r = spacing * theta;
      cx = r * Math.cos(direction * theta) * 1.1;
      cy = r * Math.sin(direction * theta) * 0.9;
      const rect = { left: cx - halfW, right: cx + halfW, top: cy - halfH, bottom: cy + halfH };
      if (insideCircle(rect) && !collides(rect)) {
        found = rect;
        break;
      }
      // Step ~3px along the curve whatever the radius.
      theta += Math.min(0.4, 3 / Math.max(r, 1));
    }

    if (!found) continue;
    const placedRect = found;
    cellsFor(placedRect).forEach((key) => {
      const bucket = grid.get(key);
      if (bucket) bucket.push(placedRect);
      else grid.set(key, [placedRect]);
    });
    placed.push({ ...word, x: cx, y: cy, baselineOffset });
  }

  return placed;
}

export interface CloudUnit {
  singular: string;
  plural: string;
}

interface AuthorCloudProps {
  authors: CloudAuthor[];
  // Called with the number of authors that didn't fit, so the page can point
  // to the full list instead of silently dropping them.
  onOverflow?: (hiddenCount: number) => void;
  // Where each name links to (its slug is appended) and what its count measures.
  basePath?: string;
  unit?: CloudUnit;
  label?: string;
}

// Circular word cloud of names: bigger names have more items (quotes, posts).
// Each name links to its page. Re-lays itself out whenever its width changes
// (phone / tablet / desktop) and follows the light/dark theme.
export function AuthorCloud({
  authors,
  onOverflow,
  basePath = "/quotes/author",
  unit = { singular: "quote", plural: "quotes" },
  label = "Author cloud - larger names have more quotes",
}: AuthorCloudProps) {
  const countText = (count: number) => `${count} ${count === 1 ? unit.singular : unit.plural}`;
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const diameter = width ? diameterFor(width) : 0;

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const measure = () => setWidth(Math.floor(element.getBoundingClientRect().width));
    measure();
    // Debounced so dragging a window edge doesn't re-run the layout per pixel.
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(measure, 150);
    });
    observer.observe(element);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  const words = useMemo(() => (diameter ? buildWords(authors, diameter) : []), [authors, diameter]);
  const placed = useMemo(() => (diameter ? layoutInCircle(words, diameter) : []), [words, diameter]);

  useEffect(() => {
    if (diameter) onOverflow?.(words.length - placed.length);
  }, [diameter, words.length, placed.length, onOverflow]);

  const openAuthor = (event: React.MouseEvent, slug: string) => {
    // Let ctrl/cmd/shift-click open a new tab/window as a normal link would.
    if (event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    navigate(`${basePath}/${encodeURIComponent(slug)}`);
  };

  const radius = diameter / 2;

  return (
    <div ref={containerRef} className="w-full flex justify-center" style={{ minHeight: diameter || 320 }}>
      {diameter > 0 && (
        <svg
          viewBox={`0 0 ${diameter} ${diameter}`}
          width={diameter}
          height={diameter}
          className="max-w-full h-auto"
          role="group"
          aria-label={label}
        >
          <circle
            cx={radius}
            cy={radius}
            r={radius - 1}
            className="fill-white stroke-slate-200 dark:fill-[#141a2e] dark:stroke-slate-700"
            strokeWidth={1.5}
          />
          <g transform={`translate(${radius},${radius})`}>
            {placed.map((word) => (
              <a
                key={word.slug}
                href={`${basePath}/${encodeURIComponent(word.slug)}`}
                onClick={(event) => openAuthor(event, word.slug)}
                aria-label={`${word.text}, ${countText(word.count)}`}
                className="cursor-pointer outline-none [&:focus-visible>text]:underline"
              >
                <title>{`${word.text} - ${countText(word.count)}`}</title>
                <text
                  textAnchor="middle"
                  y={word.baselineOffset}
                  transform={`translate(${word.x},${word.y}) rotate(${word.rotate})`}
                  className={`${word.colorClass} transition-opacity duration-200 hover:opacity-70`}
                  style={{ fontSize: word.size, fontFamily: word.font, fontWeight: word.weight }}
                >
                  {word.text}
                </text>
              </a>
            ))}
          </g>
        </svg>
      )}
    </div>
  );
}
